import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PortalPreview from '../components/portal/composer/PortalPreview';
import StepPick from '../components/portal/composer/StepPick';
import { MAX_CLIP_MS as DEFAULT_MAX_CLIP_MS, fmtClip } from '../components/portal/composer/StepTrim';
import TrimCoverSheet from '../components/portal/composer/TrimCoverSheet';
import { EventSheet, VenueSheet, CitySheet } from '../components/portal/composer/LinkSheet';
import WindowSheet from '../components/portal/composer/WindowSheet';
import SourcesSheet from '../components/portal/composer/SourcesSheet';
import messageService from '../services/messageService';
import SignalFeedCard from '../components/SignalFeedCard';
import ScaledCard from '../components/share/ScaledCard';
import useShareTemplates from '../hooks/useShareTemplates';
import SendPanel, { AUDIENCES, sendSummary } from '../components/portal/composer/SendPanel';
import useVideoFrames from '../components/portal/composer/useVideoFrames';
import SendingTakeover from '../components/portal/composer/SendingTakeover';
import { XIcon, ChevronLeftIcon } from '../components/portal/canvas/icons';
import { usePortalPosting } from '../context/PortalPostingContext';
import { useDialog } from '../context/DialogContext';
import { useUser } from '../context/UserContext';
import useMediaQuery from '../utils/useMediaQuery';
import { safePad } from '../utils/safeArea';
import { trackEvent } from '../services/analytics';
import storyService from '../services/storyService';
import api from '../services/api';
import { realPlace, isPlaceholderPlace } from '../utils/placeLabel';
import { FRAME_DEFAULT, frameForPublish } from '../utils/mediaFrame';
import { normalizeLayout, layoutForPublish } from '../utils/portalLayout';
import { DEFAULT_WINDOW, windowLabel, windowShort, windowIso, windowDateBits } from '../utils/portalWindow';
import { NO_WORDS_HEADLINE } from '../utils/portalWords';

/**
 * CREATE A PORTAL — three steps: PICK · MAKE · SEND, plus the required TRIM + COVER sheet for
 * a video. Handoff: docs/plans/2026-09-portal-composer-v3/handoff/README.md.
 *
 * One full-bleed canvas shared by every step. PICK lays its panel over it, MAKE is the canvas
 * itself (type on the Portal, style it, place it, link an event and a venue, set a window from
 * the right rail), SEND lays the audience panel over it. Full screen on a phone; on a desktop
 * the same 400×820 frame, centred.
 *
 * The one rule that shapes everything: **nothing is uploaded until SEND.** The file is read
 * locally, the cover frames come off that local copy, and the first byte leaves the device when
 * they send — at which point the composer closes, they land back on the wall, and
 * PortalPostingContext carries the upload the rest of the way.
 */
const LOCATION_KEY = 'pr.userLocation';
const STEP_LABEL = ['PICK', 'MAKE', 'OPEN'];
/** Broadest first — the singular `audience` the API keeps for one-room sends. */
const ROOM_PRIORITY = ['public', 'friends', 'club', 'close_friends'];

function readLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const town = (label) => (label || '').split(',')[0].trim();

/** The API's rooms for the panel's picks: YOUR CITY ⇒ public, and the clubs add `club`. */
export function roomsFor(aud, clubIds) {
  const rooms = [];
  if (aud.includes('town')) rooms.push('public');
  if (aud.includes('friends')) rooms.push('friends');
  if (aud.includes('close')) rooms.push('close_friends');
  if (clubIds.length) rooms.push('club');
  const ordered = ROOM_PRIORITY.filter((r) => rooms.includes(r));
  return { audience: ordered[0] || 'friends', audiences: ordered.length ? ordered : ['friends'] };
}

export default function PortalComposerPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { showConfirm } = useDialog();
  const { startPosting } = usePortalPosting();
  // The canvas is a preview of YOUR Portal, so it wears your face and your name.
  const { profile } = useUser();

  // "Broadcast as Portal" from an event card still pre-fills the words and the link.
  const fromEvent = routerLocation.state?.fromEvent || null;
  // SEND on the City Portal's OPEN A PORTAL: the words come with it and so does the square —
  // YOUR CITY is already ticked (it can be turned off). It still opens on PICK.
  const fromSquare = routerLocation.state?.fromSquare || null;
  // Plan detection (Oct 2026): OPEN A PORTAL on a chat's suggestion. Everything is filled in — the
  // words, the venue (locked), the window, who sees it, who's invited — and it opens on OPEN
  // (step 3). Back goes to MAKE, where VENUE and WINDOW wear the linked ring.
  const fromChat = routerLocation.state?.fromChat || null;

  // OPEN A PORTAL on an event card: the poster is the picture. It shows at once from its own URL
  // and becomes a real local file a moment later (fetched through our API — most poster hosts
  // won't let the browser read them), so SEND uploads it like any photo you'd picked.
  const [media, setMedia] = useState(() => (
    fromEvent?.posterUrl && fromEvent?.eventId
      ? { kind: 'photo', localUrl: fromEvent.posterUrl, file: null, name: null, fromPoster: true }
      : { kind: 'text', localUrl: null, file: null, name: null }
  ));
  const [coverPct, setCoverPct] = useState(0.3);
  // Which slice of the file the Portal is. A clip is capped (30s by default, an admin can
  // raise it) and TRIM is never skipped — it is also how you cut the dead air off the front.
  const [maxClipMs, setMaxClipMs] = useState(DEFAULT_MAX_CLIP_MS);
  const [trim, setTrim] = useState({ startMs: 0, endMs: 0 });
  const [trimOk, setTrimOk] = useState(false);
  const [gateTab, setGateTab] = useState('trim');
  const [playSec, setPlaySec] = useState(0);
  // Mid-drag on TRIM: the frame under the handle, which the canvas holds on. Null otherwise.
  const [scrubMs, setScrubMs] = useState(null);
  // Where in the 9:16 frame the round ring should look. The middle is rarely the subject.
  const [coverFocus, setCoverFocus] = useState({ x: 50, y: 50 });
  // Where the photo or clip sits in the frame (pan + zoom); its rotation lives on the layout.
  const [frame, setFrame] = useState(FRAME_DEFAULT);
  const [layout, setLayout] = useState(() => normalizeLayout(null));
  const [text, setText] = useState(fromEvent?.defaultText || fromSquare?.defaultText || fromChat?.text || '');
  // …and the event it came from is already linked.
  const [event, setEvent] = useState(() => (fromEvent?.eventId ? {
    id: fromEvent.eventId,
    title: fromEvent.title,
    startsAtUtc: fromEvent.startUtc || null,
    imageUrl: fromEvent.posterUrl || null,
    meta: fromEvent.locationLabel || '',
    place: fromEvent.locationLabel || '',
  } : null));
  const [win, setWin] = useState(() => (fromChat?.window?.on ? fromChat.window : DEFAULT_WINDOW));
  const [city, setCity] = useState(() => {
    const fc = fromChat?.city;
    if (fc?.label && !isPlaceholderPlace(fc.label)) return { label: fc.label, lat: fc.lat ?? null, lng: fc.lng ?? null };
    const sq = fromSquare?.city;
    if (sq?.label && !isPlaceholderPlace(sq.label)) return { label: sq.label, lat: sq.lat ?? null, lng: sq.lng ?? null };
    const loc = readLocation();
    // A placeholder is not a city: sent as one it became the Portal's city and a square's name.
    return loc ? { label: realPlace(loc.label), lat: loc.lat, lng: loc.lng } : null;
  });
  const [venue, setVenue] = useState(() => (fromChat?.venue?.lat != null && fromChat?.venue?.lng != null ? { name: fromChat.venue.name, address: fromChat.venue.address || '', lat: fromChat.venue.lat, lng: fromChat.venue.lng } : null));
  // The venue lock (venue-lock handoff): off ⇒ the place shows once the host lets them in. The
  // tooltip asks once per draft, the first time a venue is added. From a chat the lock stays on
  // (the chat was private) and the tooltip is skipped — the sources sheet says where it came from.
  const [venueOpen, setVenueOpen] = useState(() => !!fromChat?.venueOpen);
  const [vTip, setVTip] = useState(false);
  const [vTipSeen, setVTipSeen] = useState(!!fromChat);
  // WHO CAN JOIN: open (everyone is welcome) or curated (they ask, you pick).
  const [joinMode, setJoinMode] = useState(fromChat?.joinMode || 'open');
  const shareDefaults = useShareTemplates();
  const [shareTemplatePick, setShareTemplatePick] = useState(null);
  const [aud, setAud] = useState(() => (fromChat?.aud?.length ? fromChat.aud : fromSquare?.townSquare ? ['town', 'friends'] : ['friends']));
  // INVITE · n FROM THE CHAT: everyone else in the chat, un-tickable.
  const [inviteIds, setInviteIds] = useState(() => fromChat?.inviteUserIds || []);
  const [clubs, setClubs] = useState([]);
  // Arriving from a club's own OPEN A PORTAL: that club is already ticked.
  const [clubIds, setClubIds] = useState(() => {
    const fromClub = new URLSearchParams(routerLocation?.search || '').get('club');
    return fromClub ? [fromClub] : (fromChat?.clubIds || []);
  });
  const [sending, setSending] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  // From an event there's nothing to pick — it opens on MAKE. From a chat it opens on OPEN.
  const [step, setStep] = useState(fromChat ? 3 : fromEvent ? 2 : 1);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(null);      // 'event' | 'venue' | null
  const [sheet, setSheet] = useState(null);            // 'event' | 'venue' | 'window' | 'city' | 'trim' | null
  const [peek, setPeek] = useState(false);

  useEffect(() => {
    let done = false;
    storyService.getCapabilities().then((r) => {
      const secs = r?.data?.maxDurationSec;
      if (!done && secs > 0) setMaxClipMs(secs * 1000);
    });
    import('../services/clubService')
      .then(({ default: clubService }) => clubService.getMyClubs())
      .then((r) => { if (!done && r?.success) setClubs((r.data?.items || r.data || []).filter((c) => c?.id)); })
      .catch(() => { /* no clubs row rather than a broken step */ });
    return () => { done = true; };
  }, []);

  const { frames, durationSec, cover, captureCover, extracting } = useVideoFrames(media.localUrl, media.kind, trim);
  const localUrlRef = useRef(null);
  const isVideo = media.kind === 'video';

  useEffect(() => {
    if (!isVideo || !durationSec) return;
    const ms = Math.round(durationSec * 1000);
    setTrim({ startMs: 0, endMs: Math.min(ms, maxClipMs) });
    setCoverPct(0.3);
  }, [durationSec, isVideo, maxClipMs]);

  useEffect(() => { if (isVideo) captureCover(coverPct); }, [coverPct, isVideo, captureCover, trim]);

  // Frame extraction that ran and found nothing is a broken file, not a slow one.
  const wasExtracting = useRef(false);
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    if (wasExtracting.current && !extracting) setAttempted(true);
    wasExtracting.current = extracting;
  }, [extracting]);
  const failed = isVideo && attempted && !extracting && !durationSec;

  // The object URL is the only copy while composing; don't leak it on the way out. Once SENT, it
  // belongs to the upload: the sender watches their own Portal from it until the encode lands,
  // and PortalPostingContext lets it go after that.
  const sentRef = useRef(false);
  useEffect(() => () => {
    if (sentRef.current) return;
    if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
  }, []);

  const pickFile = useCallback((file) => {
    const kind = file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'photo' : null;
    if (!kind) return;
    if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
    const url = URL.createObjectURL(file);
    localUrlRef.current = url;
    setMedia({ kind, localUrl: url, file, name: file.name });
    setFrame(FRAME_DEFAULT);
    setLayout((l) => ({ ...l, rot: { ...l.rot, media: 0 } }));
    setCoverPct(0.3);
    setCoverFocus({ x: 50, y: 50 });
    setTrim({ startMs: 0, endMs: 0 });
    setTrimOk(false);
    setGateTab('trim');
    setAttempted(false);
  }, []);

  // Fetch the event's poster as a file. If it can't be had, fall back to a text Portal rather
  // than sending a picture that was never really there.
  useEffect(() => {
    if (!media.fromPoster || !fromEvent?.eventId) return undefined;
    let cancelled = false;
    api.get(`/events/${fromEvent.eventId}/poster-image`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blob = res.data;
        const type = blob?.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
        const ext = type.split('/')[1] || 'jpg';
        pickFile(new File([blob], `poster.${ext}`, { type }));
      })
      .catch(() => {
        if (!cancelled) setMedia((m) => (m.fromPoster ? { kind: 'text', localUrl: null, file: null, name: null } : m));
      });
    return () => { cancelled = true; };
    // Once, on the way in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickText = useCallback(() => {
    if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
    localUrlRef.current = null;
    setMedia({ kind: 'text', localUrl: null, file: null, name: null });
    setTrimOk(false);
  }, []);

  // ---- steps ----
  const gateLocked = sheet === 'trim' && !trimOk;

  const go = useCallback((n) => {
    let next = Math.max(1, Math.min(3, n));
    if (next === 3 && isVideo && !trimOk) next = 2;
    const gate = next === 2 && isVideo && !trimOk;
    setEditing(false);
    setSelected(null);
    setPeek(false);
    setStep(next);
    setSheet(gate ? 'trim' : null);
    if (gate) setGateTab('trim');
  }, [isVideo, trimOk]);

  const close = async () => {
    if (text || media.file) {
      const ok = await showConfirm({
        title: 'LEAVE THIS PORTAL?',
        message: "Nothing has been sent. You'll lose what you wrote.",
        confirmLabel: 'LEAVE',
        cancelLabel: 'KEEP WRITING',
        variant: 'danger',
      });
      if (!ok) return;
    }
    leave();
  };

  /**
   * Back where they came from. Popping history is what restores the wall's scroll position —
   * pushing /featured would land them at the top of a freshly loaded feed instead of the card
   * they were looking at when they started.
   */
  function leave() {
    const idx = window.history.state && typeof window.history.state.idx === 'number' ? window.history.state.idx : 0;
    if (idx > 0) navigate(-1); else navigate('/featured');
  }

  const back = () => { if (step === 1) close(); else go(step - 1); };

  // ---- the sheets ----
  const openSheet = (kind) => { setSelected(null); setSheet(kind); if (kind === 'trim') setGateTab('trim'); };
  const closeSheet = () => { if (!gateLocked) setSheet(null); };
  const removeLink = (kind) => {
    if (kind === 'event') setEvent(null);
    if (kind === 'venue') setVenue(null);
    setSheet(null);
    setSelected(null);
  };
  const dismissTip = () => { setVTip(false); setVTipSeen(true); };
  const changeVenueOpen = (v) => { setVenueOpen(v); dismissTip(); };
  const pickCity = (c) => {
    const next = { slug: c.slug || null, label: c.label, lat: c.lat ?? null, lng: c.lng ?? null };
    // A venue in the city you just left is not where you are.
    if (town(next.label).toLowerCase() !== town(city?.label).toLowerCase()) setVenue(null);
    setCity(next);
  };

  // ---- send ----
  const clipSec = Math.max(0, (trim.endMs - trim.startMs) / 1000);
  const label = windowLabel(win);
  const summary = sendSummary(aud, clubIds.length, label, joinMode);
  const roomLabel = [
    AUDIENCES.filter((a) => aud.includes(a.key)).map((a) => a.label).join(' + ') || null,
    clubIds.length ? (clubIds.length === 1 ? 'CLUB' : `${clubIds.length} CLUBS`) : null,
  ].filter(Boolean).join(' + ');

  const needsWords = media.kind === 'text' && !text.trim();

  const send = () => {
    if (sending || (aud.length === 0 && clubIds.length === 0)) return;
    // Nothing to show and nothing said: back to the words, not a post that can only fail.
    if (needsWords) { go(2); setEditing(true); return; }
    setSending(true);
    sentRef.current = true;
    trackEvent('portal_send', { kind: media.kind, hasEvent: !!event, townSquare: aud.includes('town') });

    // The rooms OR together — a reader in any one of them sees it. People invited from a chat
    // are the API's own picked-people room (`select` + selectedUserIds).
    const { audience, audiences } = roomsFor(aud, clubIds);
    const { startUtc, endUtc } = windowIso(win);
    const invited = fromChat ? inviteIds.filter(Boolean) : [];
    const signalRequest = {
      signalText: text.trim(),
      audience,
      audiences: invited.length ? [...audiences, 'select'] : audiences,
      selectedUserIds: invited.length ? invited : null,
      radius: 'local',
      mode: joinMode,
      layout: layoutForPublish(layout, { textOnly: media.kind === 'text', hasVenue: !!venue, hasEvent: !!event }),
      sourceEventId: event?.id || fromEvent?.eventId || null,
      latitude: venue?.lat ?? city?.lat ?? null,
      longitude: venue?.lng ?? city?.lng ?? null,
      locationLabel: city?.label || null,
      // No window ⇒ live now, gone in four hours; the server owns that default.
      startUtc,
      endUtc,
      venueName: venue?.name || null,
      venueAddress: venue?.address || null,
      venueOpen: !!venue && venueOpen,
      clubIds: clubIds.length ? clubIds : null,
      shareTemplate: shareTemplatePick,
    };

    // Deliberately not awaited: the upload runs underneath the takeover from this moment, and
    // a failure is reported by the sender's own posting state on Featured — never by
    // interrupting the animation.
    const posting = startPosting({
      media: { ...media, clip: isVideo && trim.endMs > trim.startMs ? trim : null },
      signalRequest,
      // The cover time is inside the clip, because that is what the clip is.
      coverMs: isVideo ? Math.round(trim.startMs + coverPct * clipSec * 1000) : null,
      coverFocus: isVideo ? coverFocus : null,
      coverUrl: isVideo ? cover : media.localUrl,
      frame: isVideo || media.kind === 'photo' ? frameForPublish(frame) : null,
      trim: isVideo && trim.endMs > trim.startMs
        ? { startMs: Math.round(trim.startMs), endMs: Math.round(trim.endMs) }
        : null,
    });
    // From a chat: once the Portal exists, the card goes back into the chat (board 1e). The chat
    // screen is where `leave()` lands, so the card is waiting there.
    if (fromChat?.threadId) {
      Promise.resolve(posting).then((r) => {
        if (r?.success && r.eventId) return messageService.planPublished(fromChat.threadId, 'portal', r.eventId);
        return null;
      }).catch(() => null);
    }

    setTakingOver(true);
  };

  // ---- the canvas ----
  const venueOnCard = useMemo(() => (venue ? { name: venue.name, address: venue.address || city?.label || '', city: town(city?.label) } : null), [venue, city?.label]);
  const venueDate = useMemo(() => windowDateBits(win), [win]);
  const seeking = sheet === 'trim' && gateTab === 'cover';
  const badge = isVideo && durationSec ? `${fmtClip(durationSec)}${durationSec * 1000 > maxClipMs + 50 ? ' · TRIM' : ''}` : null;

  const canvas = (
    <PortalPreview
      media={media}
      text={text}
      chip={label}
      layout={layout}
      onLayout={setLayout}
      venue={venueOnCard}
      venueDate={venueDate}
      event={event}
      editor={{
        onText: setText,
        editing,
        onEditing: setEditing,
        selected,
        onSelected: setSelected,
        onOpen: openSheet,
        onRemove: removeLink,
        frame,
        onFrame: media.kind === 'text' ? null : setFrame,
        isVideo,
        windowOn: win.on,
        locked: step !== 2,
        onTapText: step === 1 ? () => { setStep(2); setEditing(true); } : null,
        showChrome: step === 2 && !sheet,
        venueOpen,
        onVenueOpen: changeVenueOpen,
        venueTip: vTip,
        onVenueTip: () => dismissTip(),
        mode: joinMode,
      }}
      coverPct={coverPct}
      seeking={seeking}
      scrubMs={sheet === 'trim' && gateTab === 'trim' ? scrubMs : null}
      durationSec={durationSec}
      clip={isVideo && trim.endMs > trim.startMs ? trim : null}
      onPlaySec={sheet === 'trim' ? setPlaySec : null}
      frame={frame}
      showHeader={step === 3}
      timeLeft={windowShort(win)}
      authorName={profile?.handle || profile?.displayName || 'you'}
      authorAvatarUrl={profile?.avatarUrl || profile?.faceUrl || null}
      className="absolute inset-0"
    />
  );

  const showNext = step < 3 && !gateLocked;
  const header = !editing && (
    <div className="absolute inset-x-0 top-0 font-mono text-white" style={{ zIndex: 8, padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 12, pointerEvents: 'none' }} data-testid="composer-header">
      <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) 56px', alignItems: 'center' }}>
        <button
          type="button"
          onClick={back}
          aria-label={step === 1 ? 'Close' : 'Back'}
          className="prc-pill"
          style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(0,0,0,.5)', color: '#fff', border: 0, borderRadius: '50%', cursor: 'pointer', pointerEvents: 'auto' }}
        >
          {step === 1 ? <XIcon size={18} /> : <ChevronLeftIcon />}
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.14em' }}>CREATE A PORTAL</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }} data-testid="step-label">{STEP_LABEL[step - 1]} · 0{step} / 03</span>
        </div>
        {showNext ? (
          <button type="button" onClick={() => go(step + 1)} style={{ justifySelf: 'end', padding: '8px 0', background: 'transparent', color: '#00FFFF', border: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer', pointerEvents: 'auto' }}>
            NEXT
          </button>
        ) : <span />}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }} aria-hidden data-testid="progress">
        {[1, 2, 3].map((n) => <div key={n} style={{ height: 3, borderRadius: 999, background: n <= step ? '#fff' : 'rgba(255,255,255,.3)' }} />)}
      </div>
    </div>
  );

  const sheetNode = sheet && !editing && (step === 2 || sheet === 'city' || sheet === 'sources') ? (() => {
    switch (sheet) {
      case 'sources':
        return <SourcesSheet chatName={fromChat?.threadName} rows={fromChat?.sources || []} onClose={closeSheet} />;
      case 'trim':
        return (
          <TrimCoverSheet
            locked={!trimOk}
            tab={gateTab}
            onTab={(t) => { setGateTab(t); if (t === 'cover' && !cover) captureCover(coverPct); }}
            onClose={closeSheet}
            onConfirm={() => { setTrimOk(true); setSheet(null); }}
            frames={frames}
            durationSec={durationSec}
            trim={trim}
            maxClipMs={maxClipMs}
            playSec={playSec}
            loading={!durationSec && !failed}
            failed={failed}
            onTrim={(next, { scrubToMs, settled }) => {
              setTrim(next);
              setPlaySec(scrubToMs / 1000);
              setScrubMs(settled ? null : scrubToMs);
              // Any change to the window puts the cover back to 30% into the NEW clip.
              if (settled) setCoverPct(0.3);
            }}
            coverPct={coverPct}
            onCoverPct={setCoverPct}
            onCoverSettled={() => captureCover(coverPct)}
            cover={cover}
            coverFocus={coverFocus}
            onCoverFocus={setCoverFocus}
          />
        );
      case 'event':
        return <EventSheet value={event} onPick={(item) => { setEvent(item); setSheet(null); setSelected('event'); }} onRemove={() => removeLink('event')} onClose={closeSheet} />;
      case 'venue':
        return (
          <VenueSheet
            value={venue}
            city={city}
            onCity={pickCity}
            onPick={(v) => { setVenue({ name: v.name, address: v.address, lat: v.lat, lng: v.lng }); setSheet(null); setSelected('venue'); setVTip(!venueOpen && !vTipSeen); }}
            onRemove={() => removeLink('venue')}
            onClose={closeSheet}
            venueOpen={venueOpen}
            onVenueOpen={changeVenueOpen}
            mode={joinMode}
          />
        );
      case 'window':
        return <WindowSheet value={win} onChange={setWin} onClose={closeSheet} />;
      case 'city':
        return <CitySheet value={city} onPick={(c) => { pickCity(c); setSheet(null); }} onClose={closeSheet} />;
      default:
        return null;
    }
  })() : null;

  const frameBody = (
    <>
      {canvas}
      {header}

      {step === 1 && (
        <div className="absolute inset-x-0 bottom-0" style={{ zIndex: 6, background: '#000', padding: safePad('18px 16px 24px') }}>
          <StepPick media={{ ...media, badge }} onPickFile={pickFile} onPickText={pickText} desktop={isDesktop} />
        </div>
      )}

      {step === 3 && (
        <>
          <div aria-hidden data-testid="peek-layer" onPointerDown={() => setPeek((p) => !p)} style={{ position: 'absolute', inset: 0, zIndex: 5 }} />
          <SendPanel
            aud={aud}
            onAud={setAud}
            clubs={clubs}
            clubIds={clubIds}
            onClubIds={setClubIds}
            cityLabel={city?.label || ''}
            onOpenCity={() => { setPeek(false); setSheet('city'); }}
            peek={peek}
            onTogglePeek={() => setPeek((p) => !p)}
            onSend={send}
            needsWords={needsWords}
            sending={sending}
            windowLabel={label}
            joinMode={joinMode}
            onJoinMode={setJoinMode}
            fromChat={fromChat ? { name: fromChat.threadName, people: fromChat.people || [] } : null}
            inviteIds={inviteIds}
            onInviteIds={setInviteIds}
            onOpenSources={() => { setPeek(false); setSheet('sources'); }}
            shareTemplate={shareTemplatePick || shareDefaults?.signal || 'ring'}
            onShareTemplate={setShareTemplatePick}
            renderCardPreview={(t) => (
              <ScaledCard box={48} height={1080}>
                <SignalFeedCard template={t} init={(profile?.displayName || '?')[0].toUpperCase()} topline={`${profile?.displayName || ''} `} words={text || (media.kind === 'text' ? 'What do you want to do?' : NO_WORDS_HEADLINE)} />
              </ScaledCard>
            )}
          />
        </>
      )}

      {sheetNode}

      {takingOver && (
        <SendingTakeover
          coverUrl={isVideo ? cover : media.localUrl}
          avatarUrl={profile?.avatarUrl || profile?.faceUrl || null}
          roomLabel={summary || roomLabel}
          onDone={leave}
        />
      )}
    </>
  );

  if (isDesktop) {
    return (
      <div className="fixed inset-0 safe-inset z-[95] flex items-center justify-center" style={{ background: '#1A1A1A' }}>
        <div className="relative overflow-hidden" style={{ width: 400, height: 'min(820px, calc(100vh - 32px))', background: '#000', outline: '2px solid #fff', userSelect: 'none' }} data-testid="composer-frame">
          {frameBody}
        </div>
      </div>
    );
  }

  return (
    // The black fills the screen edge to edge — a story composer should — but the
    // CONTROLS have to sit between the status bar and the navigation bar. Padding
    // the fixed container does both: the background still paints under the system
    // bars, and absolutely-positioned children resolve against the padding box, so
    // everything inside inherits the inset without touching a single child.
    //
    // The featured wall gets this from TopBar's .pt-safe-top and MobileTabBar's
    // own padding; this overlay sits above both and had nothing.
    <div
      className="fixed inset-0 safe-inset z-[95] overflow-hidden"
      style={{ background: '#000', userSelect: 'none' }}
      data-testid="composer-frame"
    >
      {frameBody}
    </div>
  );
}
